// src/shared/utils/date.ts

import { DateTime } from 'luxon';

/**
 * Format Unix timestamp (seconds) for display using the user's locale.
 * Full datetime — e.g., "1/15/2024, 9:30 AM" (en-US).
 * Used for tooltips and detail views where exact time matters.
 */
export function formatTimestamp(timestamp: number): string {
  return DateTime
    .fromSeconds(timestamp)
    .toLocaleString(DateTime.DATETIME_SHORT);
}

/**
 * Format Unix timestamp as relative time (e.g., "2 hours ago").
 */
export function formatRelative(timestamp: number): string {
  return DateTime
    .fromSeconds(timestamp)
    .toRelative() ?? '';
}

/**
 * Format Unix timestamp as date only (e.g., "1/15/2024").
 */
export function formatDate(timestamp: number): string {
  return DateTime
    .fromSeconds(timestamp)
    .toLocaleString(DateTime.DATE_SHORT);
}

/**
 * Smart compact date for data grid cells.
 * Adapts format based on recency for maximum information density:
 *   < 1 hour:  "12m ago"
 *   < 24 hours: "5h ago"
 *   < 7 days:  "3d ago"
 *   Same year: "Jan 15"
 *   Older:     "Jan 15, 2024"
 *
 * Pair with formatTimestamp() in a tooltip for full datetime on hover.
 */
export function formatSmartDate(timestamp: number): string {
  const dt = DateTime.fromSeconds(timestamp);
  const now = DateTime.now();
  const diff = now.diff(dt, ['hours', 'days']).toObject();
  const hours = diff.hours ?? 0;
  const days = diff.days ?? 0;

  if (days < 0 || hours < 0) {
    // Future date — show absolute
    return dt.year === now.year
      ? dt.toFormat('MMM d')
      : dt.toFormat('MMM d, yyyy');
  }

  if (days === 0 && hours < 1) {
    const minutes = Math.max(1, Math.floor(now.diff(dt, 'minutes').minutes));
    return `${minutes}m ago`;
  }

  if (days === 0) {
    return `${Math.floor(hours)}h ago`;
  }

  if (days < 7) {
    return `${Math.floor(days)}d ago`;
  }

  if (dt.year === now.year) {
    return dt.toFormat('MMM d');
  }

  return dt.toFormat('MMM d, yyyy');
}

/**
 * The backend represents "open-ended / no expiration" dates with a far-future
 * sentinel (e.g. billing `expires` = 253402214400 = 9999-12-31T00:00:00Z, which
 * renders literally as "Dec 30, 9999" in negative-offset zones). Treat any year
 * >= 9999 as a sentinel, along with non-positive / non-finite / nullish values.
 */
export function isSentinelDate(timestamp: number | null | undefined): boolean {
  if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp <= 0) {
    return true;
  }
  return DateTime.fromSeconds(timestamp).year >= 9999;
}

/**
 * Format a billing-style date that may carry the open-ended sentinel. Returns
 * null for sentinels/invalid values so callers can hide the line or show a
 * placeholder; real dates render as a medium date (e.g. "Jul 4, 2026").
 */
export function formatBillingDate(timestamp: number | null | undefined): string | null {
  if (isSentinelDate(timestamp)) return null;
  return DateTime.fromSeconds(timestamp as number).toLocaleString(DateTime.DATE_MED);
}
